/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2015, 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'underscore',
	'../../../widgets/livewidget/nls/StringResources',
	'../../../util/DashboardFormatter'
], (_, stringResources, Formatter) => {

	const inFilter = (filter, columnMetadata, op, valuesList) => {
		let values = valuesList || filter.values;
		if (values.toJSON) {
			values = values.toJSON();
		}
		let format = null;
		if (columnMetadata.getDataType() !== 'string' && columnMetadata.getType() !== 'attribute') {
			format = {
				type: columnMetadata.getDataType()
			};
		}

		values = _.map(_.pluck(values, 'd'), (displayValue) => {
			return Formatter.format(displayValue, format);
		});

		const retval = {};
		retval[op] = values.join(stringResources.get('listSeparator') + ' ');

		return retval;
	};

	const rangeFilter = (filter, columnMetadata, lower, upper, isDateType) => {
		let values = filter.values;
		if (values.toJSON) {
			values = values.toJSON();
		}

		values = _.pluck(values, 'd');

		const spec = {
			type: columnMetadata.getDataType()
		};
		const formatInfo = columnMetadata.getFormat();
		if (formatInfo && formatInfo.maximumFractionDigits) {
			spec.maximumFractionDigits = formatInfo.maximumFractionDigits;
		}

		const retval = {};
		if (lower) {
			retval.lowerBound = Formatter.format(values[0], spec);
		}
		if (upper) {
			const index = isDateType ? 1 : (lower ? 1 : 0);
			retval.upperBound = Formatter.format(values[index], spec);
		}
		return retval;
	};

	const operators = {
		'in': {
			template: 'dimFilterIn',
			params: (filter, columnMetadata, explicitValuesList) => {
				return inFilter(filter, columnMetadata, 'in', explicitValuesList);
			}
		},
		'isnull': {
			template: 'dimFilterIn',
			params: (filter, columnMetadata) => {
				return inFilter(filter, columnMetadata, 'in');
			}
		},
		'notin': {
			template: 'dimFilterNotIn',
			params: (filter, columnMetadata, explicitValuesList) => {
				return inFilter(filter, columnMetadata, 'out', explicitValuesList);
			}
		},
		'between': {
			template: 'measureFilterBetween',
			params: (filter, columnMetadata) => {
				return rangeFilter(filter, columnMetadata, 1, 1);
			}
		},
		'notbetween': {
			template: 'measureFilterNotBetween',
			params: (filter, columnMetadata) => {
				return rangeFilter(filter, columnMetadata, 1, 1);
			}
		},
		'gt': {
			template: 'gtFilter',
			params: (filter, columnMetadata) => {
				return rangeFilter(filter, columnMetadata, 1, 0);
			}
		},
		'lt': {
			template: 'ltFilter',
			params: (filter, columnMetadata) => {
				return rangeFilter(filter, columnMetadata, 0, 1);
			}
		},
		'before': {
			template: 'ltFilter',
			params: (filter, columnMetadata) => {
				return rangeFilter(filter, columnMetadata, 0, 1, /*isDateType*/ true);
			}
		},
		'after': {
			template: 'gtFilter',
			params: (filter, columnMetadata) => {
				return rangeFilter(filter, columnMetadata, 1, 0, /*isDateType*/ true);
			}
		}
	};

	class FilterLabel {

		getFilterLabel(filterOrFilterJSON, columnMetaData) {
			const filter = (filterOrFilterJSON.toJSON) ? filterOrFilterJSON.toJSON() : filterOrFilterJSON;
			const hasFilterValues = filter.values && filter.values.length > 0;
			if (filter.operator) {
				if (filter.valueDataItem && !hasFilterValues) {
					return stringResources.get('filter_allMembersOfSet');
				}
				if (filter.operator === 'notin' && !hasFilterValues && !filter.conditions) {
					return stringResources.get('filterAll');
				}
				const op = operators[filter.operator];
				const filterLabelArray = [];
				if (filter.conditions) {
					filterLabelArray.push(stringResources.get('filterConditionLabel'));
				}
				if (hasFilterValues) {
					filterLabelArray.push(stringResources.get(op.template, op.params(filter, columnMetaData)));
				}
				if (filter.excludedValues && filter.excludedValues.length) {
					const operatorForExcludedValues = filter.operator === 'in' ? 'notin' :
						(filter.operator === 'notin') ? 'in' : null;
					if (operatorForExcludedValues) {
						const opForExcludedValues = operators[operatorForExcludedValues];
						filterLabelArray.push(stringResources.get(opForExcludedValues.template, opForExcludedValues.params(filter, columnMetaData, filter.excludedValues)));
					}
				}
				return filterLabelArray.join(stringResources.get('listSeparator') + ' ');
			} else if (Array.isArray(filter.values) && filter.values.length === 0) {
				return stringResources.get('filter_includesAll');
			} else {
				return stringResources.get('modelFilter');
			}
		}

		getDataPointFilterSummary(filterValueSummary) {
			const pointsArray = [];
			if (Array.isArray(filterValueSummary) && Array.isArray(filterValueSummary[0])) {
				//Convert from "PageContextItem dataPoint form" (a dataPoint is an array of arrays) [[{u:,d:},{u:,d:}], ]
				//to 	"valueP1V1:valueP1V2, valueP2V1:valueP2V2" for reporting as the details of a data point filter.
				_.each(filterValueSummary, (point) => {
					const pointArray = [];
					_.each(point, (tuplePart) => {
						pointArray.push(tuplePart.d);
					});
					pointsArray.push(pointArray.join(stringResources.get('pointSeparator')));
				});
			} else {
				//Convert from "filter form" [{ idP1V1: 'valueP1V1', idP1V2: 'valueP1V2'}, { idP2V1: 'valueP2V1', idP2V2: 'valueP2V2'} ... ]
				//to 	"valueP1V1:valueP1V2, valueP2V1:valueP2V2" for reporting as the details of a data point filter.
				_.each(filterValueSummary, (point) => {
					const pointArray = [];
					_.each(_.keys(point), (id) => {
						pointArray.push(point[id].d);
					});
					pointsArray.push(pointArray.join(stringResources.get('pointSeparator')));
				});
			}
			return pointsArray.join(stringResources.get('listSeparator') + ' ');
		}
	}
	return FilterLabel;
});